import { useAuth } from "@/contexts/AuthContext";
import { useAPI } from "@/hooks/useAPI";
import { FirmServiceV2 } from "@/services/firmServiceV2";
import type {
  FirmSettingsResponse,
  UpdateFirmSettingsRequest,
} from "@/services/firmServiceV2.types";
import { useCallback, useMemo } from "react";
import useSWR, { mutate } from "swr";

import type { FirmSettingsResult } from "./useFirmSettings.types";

const FIRM_SETTINGS_KEY = "/firm-settings";

export const useFirmSettings = (): FirmSettingsResult => {
  const api = useAPI("v2");
  const { firmContext } = useAuth();
  const firmService = useMemo(() => new FirmServiceV2(api), [api]);
  const firmId = firmContext?.firmId;

  const { data, error, isLoading } = useSWR<FirmSettingsResponse>(
    firmId ? [FIRM_SETTINGS_KEY, firmId] : null,
    () => {
      if (!firmId) throw new Error("Firm context is not available");
      return firmService.getFirmSettings(firmId);
    },
    {
      onError: (fetchError) =>
        console.error("[useFirmSettings] Failed to load firm settings:", fetchError),
    }
  );

  const saveFirmSettings = useCallback(
    async (values: UpdateFirmSettingsRequest): Promise<void> => {
      if (!firmId) throw new Error("Firm context is not available");
      await firmService.updateFirmSettings(firmId, values);
      await invalidateFirmSettingsCache();
    },
    [firmId, firmService]
  );

  return { firmSettings: data ?? null, isLoading, isError: Boolean(error), saveFirmSettings };
};

export const invalidateFirmSettingsCache = async (): Promise<void> => {
  await mutate((key) => Array.isArray(key) && key[0] === FIRM_SETTINGS_KEY);
};
